import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { followHandoff } from '@/auth/handoff'
import { DEFAULT_HOME } from '@/auth/routes'
import { MIN_PASSWORD } from '@/lib/password'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { PasswordField } from '@/ui/PasswordField'
import { InlineError } from '@/ui/states'
import { useBlurValidation } from '@/ui/validation'
import { PublicPage } from './PublicPage'
import { useToken } from './useToken'

/**
 * `/reset-password` — the link `password_reset_link()` builds.
 *
 * The one token page that takes input, and the one that **lands the user signed
 * in**: the reset response sets the session cookie, which is what S-D2 screen
 * 09's "Set password & log in" promises — spelled *sign in* here, on the
 * owner's terminology decision of 2026-09-06 (`docs/MOCKUP-GAP-AUDIT.md` V5(b)). It is also why the token is spent on
 * submit rather than on load — there is nothing to redeem until there is a new
 * password to redeem it with.
 *
 * Setting a password revokes every other session, including any an attacker
 * holds. That is stated on the screen, because it is the reason a reset is
 * worth doing after a compromise rather than a courtesy note.
 */
export function ResetPassword(): JSX.Element {
  const token = useToken()
  const navigate = useNavigate()
  const { adopt } = useAuth()

  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  // AUTH-16. Checked here rather than server-side because the server is never
  // sent the second field: a typo confirmation exists to catch a typo, and
  // posting both would make the API carry a rule that belongs to the form.
  //
  // Shown **on blur and then live**, which is S-D2 screen 09's own behaviour
  // and MOCKUP-METHOD's rule. It used to be computed only in `onSubmit` and
  // cleared on the next keystroke, which is the worst of both: the mismatch was
  // announced after a failed submit and then hidden again the moment somebody
  // started fixing it, so the message was never on screen while it was true.
  //
  // Silent while the confirmation is empty — that is "not filled in yet", which
  // `required` already covers, not "different from the password above".
  const mismatchRule = useBlurValidation(
    confirmation !== '' && confirmation !== password ? "These don't match." : undefined,
  )

  const reset = useMutation((body: { token: string; password: string }) =>
    call(() => api.POST('/api/v1/auth/password/reset', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (!token) return
    if (password !== confirmation) {
      // A submit is a departure from the field too, so the rule is made visible
      // rather than merely enforced — otherwise Enter from the first box would
      // do nothing at all, with no message to say why.
      mismatchRule.onBlur()
      return
    }
    const result = await reset.run({ token, password })
    if (result.ok) {
      // The reset link points at the organisation's own host, where the new
      // session is bound and `handoff_url` is null. Opened on the apex — an
      // older email, a pasted link — the answer names the host to continue
      // on, and it is a different origin (see `followHandoff`).
      if (followHandoff(result.data.handoff_url)) return
      adopt({ user: result.data.user, session: result.data.session })
      void navigate(DEFAULT_HOME, { replace: true })
    }
  }

  if (!token) {
    return (
      <PublicPage title="That link is incomplete">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            The address in your browser has no reset code in it. Ask for a new link and open the
            newest message.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/forgot-password">
            Send a new link
          </Link>
        </div>
      </PublicPage>
    )
  }

  return (
    /*
      AUTH-59: "Choose a new password" — the design's title, and the better
      one. This screen is reached by opening a link from your own mailbox, so
      the reader is not being told to do a task; they are picking a password.

      AUTH-62: no page footer. The app offered "Remembered it after all? Sign
      in", which is an offer to abandon a reset the reader is one field away
      from finishing — and the token in the URL is spent whether they take it
      or not, so the way back it advertises leads to a link that no longer
      works. Screen 09 draws no footer for that reason.
    */
    <PublicPage title="Choose a new password">
      {/* `method="post"` so a submit with dead JavaScript cannot put this password —
          or the reset token already in the URL — in the query string. Reasoning in
          full on `Login.tsx`'s form. */}
      <form
        method="post"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          <InlineError failure={reset.state.failure} />
          <PasswordField
            label="New password"
            required
            autoFocus
            minLength={MIN_PASSWORD}
            autoComplete="new-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
              reset.reset()
            }}
          />
          {/* AUTH-60: "Confirm password". "Type it again" describes the action
              rather than naming the box, which is what a label is for — and it
              is the one label on the screen a password manager reads to decide
              what to fill. */}
          <TextField
            label="Confirm password"
            type="password"
            required
            autoComplete="new-password"
            error={mismatchRule.shown}
            value={confirmation}
            onBlur={mismatchRule.onBlur}
            onChange={(event) => {
              setConfirmation(event.target.value)
            }}
          />
          {/* AUTH-34 / AUTH-35: in the body, `lg` and full width. */}
          <Button
            tone="primary"
            size="lg"
            block
            type="submit"
            busy={reset.pending}
            busyLabel="Setting password"
          >
            Set password &amp; sign in
          </Button>
          {/* AUTH-61. **After** the CTA, last in the card. It was above it,
              which put a consequence between the reader and the button — and
              read as a warning to clear before continuing rather than as a note
              about what the button will do. Screen 09 has it as the closing
              line, and the copy is the design's: shorter, and it says *devices*
              rather than enumerating browsers and phones. */}
          <p className="ar-field__help" style={{ margin: 0 }}>
            Setting a new password signs you out of all other devices.
          </p>
        </div>
      </form>
    </PublicPage>
  )
}
